import { LIQUID_MIN_CORES } from '@config/glass';
import { useAppStore } from '@stores/index';
import type { GlassMode } from '@typings/glass';

const REDUCED_TRANSPARENCY_QUERY = '(prefers-reduced-transparency: reduce)';
const FINE_POINTER_QUERY = '(pointer: fine)';

/** Attribute on <html> that the glass stylesheet branches on. */
const GLASS_ATTRIBUTE = 'data-glass';

/**
 * Present on <html> when the visitor asks for reduced transparency. The
 * themes respond with a much heavier tint (see base/_themes.scss).
 */
const REDUCED_TRANSPARENCY_ATTRIBUTE = 'data-reduced-transparency';

const supportsBackdropBlur = (): boolean =>
  CSS.supports('backdrop-filter', 'blur(1px)') ||
  CSS.supports('-webkit-backdrop-filter', 'blur(1px)');

/**
 * Only Chromium accepts an SVG filter as a backdrop-filter, and there is no
 * CSS feature query that tells the truth about it: Safari and Firefox parse
 * `backdrop-filter: url(#x)` happily and then render nothing.
 *
 * `navigator.userAgentData` exists only in Chromium-based browsers, which
 * makes it an honest engine signal without parsing a user-agent string.
 */
const isChromium = (): boolean => 'userAgentData' in navigator;

/**
 * Liquid refraction costs one extra full-resolution backdrop pass per
 * surface, so it is reserved for desktops with headroom. Touch devices get
 * frost: CLAUDE.md's mobile rule is no unnecessary GPU effects.
 */
const canAffordLiquid = (): boolean =>
  window.matchMedia(FINE_POINTER_QUERY).matches &&
  navigator.hardwareConcurrency >= LIQUID_MIN_CORES;

/**
 * Resolves how glass renders on this device and keeps it current.
 *
 * Runs before React's first render (from main.tsx) so no surface ever
 * mounts in one mode and then swaps to another.
 */
class GlassManager {
  private mediaQuery: MediaQueryList | null = null;

  private readonly handleChange = (): void => {
    this.apply(this.detect());
  };

  private get prefersReducedTransparency(): boolean {
    return this.mediaQuery?.matches ?? false;
  }

  init(): void {
    if (this.mediaQuery) return;
    this.mediaQuery = window.matchMedia(REDUCED_TRANSPARENCY_QUERY);
    this.mediaQuery.addEventListener('change', this.handleChange);
    this.apply(this.detect());
  }

  dispose(): void {
    this.mediaQuery?.removeEventListener('change', this.handleChange);
    this.mediaQuery = null;
  }

  private detect(): GlassMode {
    /* Development only: `?glass=liquid|frost|solid` forces a mode, so every
       render path can be checked on one machine. Statically removed from
       production builds. */
    if (import.meta.env.DEV) {
      const forced = new URLSearchParams(window.location.search).get('glass');
      if (forced === 'liquid' || forced === 'frost' || forced === 'solid') return forced;
    }

    if (!supportsBackdropBlur()) return 'solid';

    /* Reduced transparency keeps the material but drops refraction, and the
       themes thicken the tint, the way Apple's own setting turns glass
       frostier rather than flat. Fully opaque surfaces were tried first and
       read as no glass at all; on Windows this preference follows the
       "Transparency effects" switch, which many people turn off for
       reasons that have nothing to do with legibility. */
    if (this.prefersReducedTransparency) return 'frost';

    if (isChromium() && canAffordLiquid()) return 'liquid';
    return 'frost';
  }

  private apply(mode: GlassMode): void {
    const root = document.documentElement;
    root.setAttribute(GLASS_ATTRIBUTE, mode);
    root.toggleAttribute(REDUCED_TRANSPARENCY_ATTRIBUTE, this.prefersReducedTransparency);
    useAppStore.getState().setGlassMode(mode);
  }
}

export const glassManager = new GlassManager();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    glassManager.dispose();
  });
}
